import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock } from "lucide-react";
import { Header, Footer } from "@/components/SiteChrome";
import api from "@/lib/api";

export default function Contact() {
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", rig: "", subject: "General", message: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            toast.success("Message sent — we'll be in touch within one business day");
        } catch {
            toast.error("Something went wrong. Try again?");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="contact-page">
            <Header />
            <div className="mx-auto grid max-w-6xl gap-16 px-4 py-20 sm:px-6 md:grid-cols-2">
                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Get in touch</div>
                    <h1 className="mt-3 font-display text-5xl uppercase italic md:text-6xl text-[hsl(var(--ink))]">Talk to a human.</h1>
                    <p className="mt-6 text-[hsl(var(--ink-soft))]">
                        Fitment question, order issue, or want in on the sponsored build club — drop us a line and
                        we'll come back inside one business day.
                    </p>
                    <dl className="mt-10 space-y-6 text-[hsl(var(--ink))]" data-testid="contact-info">
                        <div className="flex items-start gap-4" data-testid="contact-info-phone">
                            <Phone className="mt-1 h-7 w-7 shrink-0 stroke-[1.5] text-[hsl(var(--ink))]" aria-hidden="true" />
                            <div>
                                <dt className="font-heading text-lg uppercase tracking-wide">Phone</dt>
                                <dd className="mt-1 font-mono-brand text-sm text-[hsl(var(--ink-soft))]">
                                    <a href="tel:+16619989179" className="hover:text-[hsl(var(--primary))]">661-998-9179</a>
                                </dd>
                            </div>
                        </div>
                        <div className="flex items-start gap-4" data-testid="contact-info-email">
                            <Mail className="mt-1 h-7 w-7 shrink-0 stroke-[1.5] text-[hsl(var(--ink))]" aria-hidden="true" />
                            <div>
                                <dt className="font-heading text-lg uppercase tracking-wide">Email</dt>
                                <dd className="mt-1 font-mono-brand text-sm text-[hsl(var(--ink-soft))]">
                                    <a href="mailto:daniel@broncooutfittersoffroad.com" className="hover:text-[hsl(var(--primary))]">daniel@broncooutfittersoffroad.com</a>
                                </dd>
                            </div>
                        </div>
                        <div className="flex items-start gap-4" data-testid="contact-info-hours">
                            <Clock className="mt-1 h-7 w-7 shrink-0 stroke-[1.5] text-[hsl(var(--ink))]" aria-hidden="true" />
                            <div>
                                <dt className="font-heading text-lg uppercase tracking-wide">Open Hours</dt>
                                <dd className="mt-1 font-mono-brand text-sm text-[hsl(var(--ink-soft))]">Monday to Friday 09:00 - 5:00</dd>
                            </div>
                        </div>
                    </dl>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="space-y-5 border-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] p-8 text-[hsl(var(--dust-50))]"
                    data-testid="contact-form"
                >
                    {sent ? (
                        <div className="text-center" data-testid="contact-sent">
                            <div className="font-display text-4xl uppercase italic">Sent. Stand by.</div>
                            <p className="mt-3 text-[hsl(var(--dust-100))]">We'll be in your inbox shortly.</p>
                        </div>
                    ) : (
                        <>
                            {[
                                { name: "name", label: "Name", type: "text" },
                                { name: "email", label: "Email", type: "email" },
                                { name: "rig", label: "Your rig (year/make/model)", type: "text" },
                            ].map((f) => (
                                <div key={f.name}>
                                    <label className="font-heading text-xs uppercase tracking-wider">{f.label}</label>
                                    <input
                                        required={f.name !== "rig"}
                                        type={f.type}
                                        name={f.name}
                                        value={form[f.name]}
                                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                        className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                        data-testid={`contact-${f.name}`}
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="font-heading text-xs uppercase tracking-wider">Subject</label>
                                <select
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    data-testid="contact-subject"
                                >
                                    <option>General</option>
                                    <option>Fitment</option>
                                    <option>Order issue</option>
                                    <option>Sponsored Build Club</option>
                                    <option>Wholesale</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-heading text-xs uppercase tracking-wider">Message</label>
                                <textarea
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    data-testid="contact-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] py-4 font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60"
                                data-testid="contact-submit"
                            >
                                {busy ? "Sending…" : "Send it →"}
                            </button>
                        </>
                    )}
                </form>
            </div>
            <Footer />
        </div>
    );
}
